import React from 'react';
import { Box, Button, Container, Typography, useTheme } from '@mui/material';
import { ArrowBack as ArrowBackIcon, ArrowForward as ArrowForwardIcon, Logout as LogoutIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useParentAuth } from '../hooks/useParentAuth';
import LanguageSelector from './LanguageSelector';
import { parentPortalSolidButtonSx, parentPortalTheme } from '../theme/parentPortalTheme';

interface ParentPortalLayoutProps {
  children: React.ReactNode;
  title?: string;
}

const shapeStyles = (item: typeof parentPortalTheme.decorative[number]) => {
  const base = {
    position: 'absolute',
    width: item.size,
    height: item.size,
    bgcolor: item.color,
    opacity: 0.75,
    pointerEvents: 'none',
    zIndex: 0,
  } as const;
  if (item.shape === 'diamond') return { ...base, transform: 'rotate(45deg)', borderRadius: '2px' };
  if (item.shape === 'triangle') {
    return {
      position: 'absolute',
      width: 0,
      height: 0,
      borderLeft: `${item.size / 2}px solid transparent`,
      borderRight: `${item.size / 2}px solid transparent`,
      borderBottom: `${item.size}px solid ${item.color}`,
      opacity: 0.75,
      pointerEvents: 'none',
      zIndex: 0,
    } as const;
  }
  return { ...base, borderRadius: '50%' };
};

const ParentPortalLayout: React.FC<ParentPortalLayoutProps> = ({ children, title }) => {
  const { t } = useTranslation('pages', { keyPrefix: 'parentPortal' });
  const { t: ta } = useTranslation('auth', { keyPrefix: 'portalLayout' });
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { logout } = useParentAuth();
  const isHome = location.pathname === '/parent/dashboard';
  const HomeIcon = theme.direction === 'rtl' ? ArrowForwardIcon : ArrowBackIcon;

  return (
    <Box sx={{ minHeight: '100vh', background: parentPortalTheme.pageBackground, position: 'relative', overflow: 'hidden' }}>
      {parentPortalTheme.backgroundOrbs.map((orb, index) => (
        <Box
          key={`orb-${index}`}
          aria-hidden
          sx={{
            position: 'absolute',
            width: orb.size,
            height: orb.size,
            borderRadius: '50%',
            bgcolor: orb.color,
            top: orb.top,
            bottom: orb.bottom,
            insetInlineStart: orb.insetInlineStart,
            insetInlineEnd: orb.insetInlineEnd,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      ))}
      {parentPortalTheme.decorative.map((item, index) => (
        <Box key={`${item.shape}-${index}`} sx={{ ...shapeStyles(item), top: item.top, bottom: item.bottom, left: item.left, right: item.right, opacity: 0.85 }} />
      ))}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: { xs: 2.5, md: 4 } }}>
        {!isHome && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 4 }}>
            <Button
              startIcon={<HomeIcon />}
              onClick={() => navigate('/parent/dashboard')}
              sx={{ ...parentPortalSolidButtonSx, px: 2.5 }}
            >
              {t('home')}
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <LanguageSelector />
              <Button
                startIcon={<LogoutIcon />}
                onClick={() => { logout(); navigate('/parent/login'); }}
                sx={{ borderRadius: 999, color: parentPortalTheme.text.primary }}
              >
                {ta('logout')}
              </Button>
              <Typography sx={{ fontWeight: 900, letterSpacing: 0, color: parentPortalTheme.text.primary }}>{t('brand')}</Typography>
            </Box>
          </Box>
        )}
        {title && !isHome && (
          <Typography variant="h4" sx={{ fontWeight: 900, textTransform: 'uppercase', mb: 3, color: parentPortalTheme.text.primary }}>
            {title}
          </Typography>
        )}
        {children}
      </Container>
    </Box>
  );
};

export default ParentPortalLayout;
